import { Component, OnInit, OnDestroy, ChangeDetectorRef } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import { Store } from "@ngrx/store";
import { Subject, forkJoin, of } from "rxjs";
import { catchError, finalize, takeUntil } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "../../shared/toastr.service";
import { EmployeesService } from "../employees.service";
import { EditableEmployee, Employee } from "../employees.interface";
import { EmployeeSetting } from "../../settings/settings.interface";
import { SettingsService } from "../../settings/settings.service";
import { AuthService } from "../../auth/auth.service";
import { StartCheckingForInstallationInProgressAction } from "../../signature-installation-state/signature-installation-state.actions";
import { BranchSettingsService } from "../../branch-settings/branch-settings.service";
import { Branch } from "../../branch-settings/branch-settings.interface";
import * as XLSX from "xlsx";

@Component({
  selector: "app-employee-manager",
  templateUrl: "./employee-manager.component.html",
  styleUrls: ["./employee-manager.component.scss"],
  standalone: false,
})
export class EmployeeManagerComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  public employees: EditableEmployee[] = [];
  public filteredEmployees: EditableEmployee[] = [];
  public isLoading = true;
  public searchTerm = "";
  public availableColumns: string[] = [];
  public employeeSettings: EmployeeSetting[] = [];
  public branchVariableNames: string[] = [];
  public branches: Branch[] = [];
  public editingCell: { row: number; column: string } | null = null;
  public isSavingAll = false;
  public isSuperAdmin = false;
  public hasRole = false;

  constructor(
    private translate: TranslateService,
    private toastr: ToastrService,
    private employeesService: EmployeesService,
    private settingsService: SettingsService,
    private store: Store,
    private dialogRef: MatDialogRef<EmployeeManagerComponent>,
    public authService: AuthService,
    private cdr: ChangeDetectorRef,
    private branchSettingsService: BranchSettingsService,
  ) {}

  ngOnInit(): void {
    this.isSuperAdmin = this.authService.isSuperAdmin;
    this.hasRole =
      this.authService.isCompanyManager ||
      this.authService.isGroupManager ||
      this.authService.isReseller;
    this.isLoading = true;
    forkJoin({
      settings: this.settingsService
        .getEmployeeSettings()
        .pipe(catchError(() => of<EmployeeSetting[]>([]))),
      employees: this.employeesService.getAllEmployeesForManager(),
      branches: this.branchSettingsService
        .fetchBranches()
        .pipe(catchError(() => of<Branch[]>([]))),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ settings, employees, branches }) => {
          this.employeeSettings = settings;
          this.branches = branches;
          this.extractBranchVariableNames();
          this.employees = employees
            .filter((emp) => emp.employeeId)
            .map((emp) => ({ ...emp, isEditing: false, isNew: false }));
          this.setupColumns();
          this.updateFilteredEmployees();
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.toastr.error(
            this.translate.instant(
              "employeeManager.errorLoadingEmployees",
            ) as string,
          );
        },
      });
  }

  private extractBranchVariableNames(): void {
    const allBranchVariableNames = new Set<string>();
    this.branches.forEach((branch) => {
      if (branch.variables) {
        Object.keys(branch.variables).forEach((varName) => {
          allBranchVariableNames.add(varName);
        });
      }
    });
    this.branchVariableNames = Array.from(allBranchVariableNames);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public addNewEmployeeRow(): void {
    const tempId = `temp-${Date.now()}`;
    const newEmployee: EditableEmployee = {
      employeeId: tempId,
      email: "",
      givenName: "",
      familyName: "",
      fullName: "",
      createdAt: new Date(),
      templateId: null,
      dynamicVariables: {},
      customLinks: [],
      vcard: [],
      badges: [],
      canBeExcludedFromSignatures: true,
      dataSource: "LOCAL",
      isNew: true,
      isEditing: true,
    };
    this.employees.unshift(newEmployee);
    this.updateFilteredEmployees();
  }

  private setupColumns(): void {
    this.availableColumns = [
      "email",
      "givenName",
      "familyName",
      "phoneNumber",
      "jobTitle",
      "mobileNumber",
    ];
    this.employeeSettings.forEach((setting) => {
      if (!this.availableColumns.includes(setting.name)) {
        this.availableColumns.push(setting.name);
      }
    });
    this.branchVariableNames.forEach((varName) => {
      if (!this.availableColumns.includes(varName)) {
        this.availableColumns.push(varName);
      }
    });
  }

  public onSearchChange(): void {
    this.updateFilteredEmployees();
  }

  private updateFilteredEmployees(): void {
    if (!this.searchTerm.trim()) {
      this.filteredEmployees = [...this.employees];
      return;
    }
    const term = this.searchTerm.toLowerCase();
    this.filteredEmployees = this.employees.filter((emp) => {
      return this.searchInEmployee(emp, term);
    });
  }

  private searchInEmployee(employee: unknown, term: string): boolean {
    if (typeof employee !== "object" || employee === null) {
      return false;
    }
    const emp = employee as Record<string, unknown>;

    for (const [key, value] of Object.entries(emp)) {
      if (
        [
          "isEditing",
          "isNew",
          "originalData",
          "validationErrors",
          "isDeleting",
          "isSaving",
        ].includes(key)
      ) {
        continue;
      }

      if (value && typeof value === "object" && !Array.isArray(value)) {
        if (this.searchInEmployee(value, term)) {
          return true;
        }
      } else if (typeof value === "string") {
        if (value.toLowerCase().includes(term)) {
          return true;
        }
      } else if (typeof value === "number") {
        if (value.toString().includes(term)) {
          return true;
        }
      }
    }
    return false;
  }

  public startEditing(row: number, column: string): void {
    const employee = this.filteredEmployees[row];

    if (column === "email" && !this.canEditEmail(employee)) {
      this.toastr.warning(
        this.translate.instant(
          "employeeManager.cannotEditEmailForExternalEmployee",
        ) as string,
      );
      return;
    }

    this.editingCell = { row, column };
    if (!employee.originalData) {
      employee.originalData = { ...employee };
    }
  }

  public saveCellValue(row: number, column: string, value: string): void {
    const employee = this.filteredEmployees[row];
    if (!employee) {
      return;
    }

    if (!employee.originalData) {
      employee.originalData = { ...employee };
    }

    const hasChanged = this.hasValueChanged(employee, column, value);

    if (this.isBasicColumn(column)) {
      (employee as unknown as Record<string, string | number | undefined>)[
        column
      ] = value;
    } else if (this.isDynamicColumn(column)) {
      if (!employee.dynamicVariables) {
        employee.dynamicVariables = {};
      }
      employee.dynamicVariables[column] = value;
    } else if (this.isBranchVariableColumn(column)) {
      if (!employee.branchVariables) {
        employee.branchVariables = {};
      }
      employee.branchVariables[column] = value;
    }

    if (hasChanged) {
      employee.isEditing = true;
    }

    const errors = this.validateEmployee(employee);
    if (Object.keys(errors).length > 0) {
      employee.validationErrors = errors;
    } else {
      delete employee.validationErrors;
    }
    this.cdr.detectChanges();
  }

  public stopEditing(): void {
    this.editingCell = null;
  }

  public cancelEditing(employee: EditableEmployee): void {
    if (employee.isNew) {
      this.removeEmployeeFromArrays(employee);
    } else if (employee.originalData) {
      Object.assign(employee, employee.originalData);
    }
    employee.isEditing = false;
    employee.originalData = undefined;
    this.editingCell = null;
  }

  public saveEmployee(employee: EditableEmployee): void {
    const errors = this.validateEmployee(employee);
    if (Object.keys(errors).length > 0) {
      employee.validationErrors = errors;
      this.toastr.error(
        this.translate.instant("employeeManager.validationErrors") as string,
      );
      return;
    }
    delete employee.validationErrors;

    if (employee.isNew) {
      this.handleNewEmployee(employee);
    } else if (!employee.employeeId) {
      this.toastr.error(
        this.translate.instant("employeeManager.invalidEmployeeId") as string,
      );
    } else {
      this.updateEmployee(employee);
    }
  }

  private handleNewEmployee(employee: EditableEmployee): void {
    this.saveEmployeeInternal(employee, true);
  }

  private updateEmployee(employee: EditableEmployee): void {
    this.saveEmployeeInternal(employee, false);
  }

  private saveEmployeeInternal(
    employee: EditableEmployee,
    isNew: boolean,
  ): void {
    employee.isSaving = true;
    const cleanEmployee = this.cleanEmployeeForApi(employee);
    const payload = { ...cleanEmployee, installSignature: false };

    const serviceCall = isNew
      ? this.employeesService.createEmployee(payload)
      : this.employeesService.updateEmployee(payload);

    serviceCall.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.toastr.success(
          this.translate.instant("employeeManager.employeeSaved") as string,
        );
        employee.isEditing = false;
        employee.originalData = undefined;
        employee.isSaving = false;
        if (isNew) {
          employee.isNew = false;
          const typedResponse = response as {
            employeeId?: string;
          };
          if (typedResponse?.employeeId) {
            employee.employeeId = typedResponse.employeeId;
          }
        }
        this.employeesService.triggerRefreshEmployees();
      },
      error: () => {
        employee.isSaving = false;
        this.toastr.error(
          this.translate.instant(
            "employeeManager.errorSavingEmployee",
          ) as string,
        );
      },
    });
  }

  public saveAllChanges(): void {
    this.saveAllChangesInternal(false);
  }

  public saveAndInstallAllChanges(): void {
    this.store.dispatch(new StartCheckingForInstallationInProgressAction());
    this.saveAllChangesInternal(true);
  }

  private saveAllChangesInternal(installSignature: boolean): void {
    const unsavedEmployees = this.employees.filter(
      (emp) => emp.isEditing || emp.isNew,
    );

    if (unsavedEmployees.length === 0) {
      this.toastr.info(
        this.translate.instant("employeeManager.noChangesToSave") as string,
      );
      return;
    }

    if (this.hasValidationErrorsInUnsaved()) {
      this.toastr.error(
        this.translate.instant(
          "employeeManager.cannotSaveWithErrors",
        ) as string,
      );
      return;
    }

    this.isSavingAll = true;

    const newEmployees = unsavedEmployees.filter((emp) => emp.isNew);
    const existingEmployees = unsavedEmployees.filter((emp) => !emp.isNew);

    const newEmployeesPromises = newEmployees.map((employee) =>
      this.saveEmployeeAsync(employee, false),
    );

    const batchUpdateRequest =
      existingEmployees.length > 0
        ? this.employeesService.batchUpdateEmployees(
            existingEmployees.map((emp) => ({
              email: emp.email,
              data: this.cleanEmployeeForApi(emp),
            })),
            installSignature,
          )
        : null;

    const allRequests = [
      ...(newEmployeesPromises.length > 0
        ? [forkJoin(newEmployeesPromises)]
        : []),
      ...(batchUpdateRequest ? [batchUpdateRequest] : []),
    ];

    if (allRequests.length === 0) {
      this.isSavingAll = false;
      return;
    }

    forkJoin(allRequests)
      .pipe(finalize(() => (this.isSavingAll = false)))
      .subscribe({
        next: (results: unknown[]) => {
          let savedCount = 0;
          let errorCount = 0;

          results.forEach((result) => {
            if (Array.isArray(result)) {
              savedCount += result.filter(
                (r: { success?: boolean }) => r.success,
              ).length;
              errorCount += result.filter(
                (r: { success?: boolean }) => !r.success,
              ).length;
            } else if (
              result &&
              typeof result === "object" &&
              "updated" in result
            ) {
              const batch = result as {
                updated: number;
                errors: Array<{ email: string; error: string }>;
              };
              savedCount += batch.updated;
              errorCount += batch.errors.length;
              if (batch.errors.length > 0) {
                batch.errors.forEach((err) => {
                  this.toastr.error(`${err.email}: ${err.error}`);
                });
              }
            }
          });

          existingEmployees.forEach((emp) => {
            emp.isEditing = false;
            emp.originalData = undefined;
            emp.isSaving = false;
          });

          if (errorCount === 0) {
            const messageKey = installSignature
              ? "employeeManager.allChangesSavedAndInstalled"
              : "employeeManager.allChangesSaved";
            this.toastr.success(
              this.translate.instant(messageKey, {
                count: savedCount,
              }) as string,
            );
            this.employeesService.triggerRefreshEmployees();
          } else {
            this.toastr.error(
              this.translate.instant("employeeManager.someChangesFailed", {
                saved: savedCount,
                failed: errorCount,
              }) as string,
            );
          }
        },
        error: () => {
          this.toastr.error(
            this.translate.instant(
              "employeeManager.errorSavingEmployee",
            ) as string,
          );
        },
      });
  }

  private saveEmployeeAsync(
    employee: EditableEmployee,
    installSignature: boolean = false,
  ): Promise<{ success: boolean }> {
    employee.isSaving = true;
    const cleanEmployee = this.cleanEmployeeForApi(employee);
    const payload = { ...cleanEmployee, installSignature };

    const serviceCall = employee.isNew
      ? this.employeesService.createEmployee(payload)
      : this.employeesService.updateEmployee(payload);

    return serviceCall
      .toPromise()
      .then((response) => {
        employee.isEditing = false;
        employee.originalData = undefined;
        employee.isSaving = false;
        if (employee.isNew) {
          employee.isNew = false;
          const typedResponse = response as {
            employeeId?: string;
          };
          if (typedResponse?.employeeId) {
            employee.employeeId = typedResponse.employeeId;
          }
        }
        return { success: true };
      })
      .catch(() => {
        employee.isSaving = false;
        return { success: false };
      });
  }

  public deleteEmployee(employee: EditableEmployee): void {
    const isLocalEmployee = employee.dataSource === "LOCAL";
    const canDelete = this.isSuperAdmin || (this.hasRole && isLocalEmployee);

    if (!canDelete) {
      if (!this.hasRole) {
        this.toastr.error(
          this.translate.instant(
            "employeeManager.onlySuperAdminCanDelete",
          ) as string,
        );
      } else {
        this.toastr.error(
          this.translate.instant(
            "employeeManager.canOnlyDeleteLocalEmployees",
          ) as string,
        );
      }
      return;
    }

    if (this.canDeleteEmployee(employee)) {
      if (employee.isNew) {
        this.removeEmployeeFromArrays(employee);
        this.toastr.success(
          this.translate.instant("employeeManager.employeeDeleted") as string,
        );
      } else {
        this.deleteEmployeeFromDatabase(employee);
      }
    } else {
      this.toastr.error(
        this.translate.instant(
          "employeeManager.cannotDeleteEmployeeWithSignature",
        ) as string,
      );
    }
  }

  private deleteEmployeeFromDatabase(employee: EditableEmployee): void {
    employee.isDeleting = true;
    this.employeesService
      .deleteEmployee(employee.employeeId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.removeEmployeeFromArrays(employee);
          this.toastr.success(
            this.translate.instant("employeeManager.employeeDeleted") as string,
          );
          this.employeesService.triggerRefreshEmployees();
        },
        error: (error) => {
          employee.isDeleting = false;
          this.toastr.error(
            ((error as { error?: { error?: string } }).error?.error ||
              this.translate.instant(
                "employeeManager.cannotDeleteEmployee",
              )) as string,
          );
        },
      });
  }

  private removeEmployeeFromArrays(employee: EditableEmployee): void {
    this.employees = this.employees.filter(
      (emp) => emp.employeeId !== employee.employeeId,
    );
    this.updateFilteredEmployees();
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file?.type === "text/csv") {
      this.parseCsvFile(file);
    } else if (file?.name.endsWith(".xlsx") || file?.name.endsWith(".xls")) {
      this.parseExcelFile(file);
    } else {
      this.toastr.error(
        this.translate.instant("employeeManager.invalidFile") as string,
      );
    }
  }

  private parseCsvFile(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      const csv = e.target?.result as string;
      if (!csv) return;

      const lines = csv.split("\n");
      const separator = lines[0].includes(";") ? ";" : ",";
      const headerLabels = lines[0]
        .split(separator)
        .map((header: string) => header.trim().replace("\r", ""));
      const headerMap = this.mapHeadersToColumns(headerLabels);

      const data = lines
        .slice(1)
        .filter((line) => line.trim())
        .map((line) => {
          const values = line.split(separator);
          const row: Record<string, string> = {};
          headerLabels.forEach((headerLabel, j) => {
            if (j < values.length) {
              const columnName = headerMap[headerLabel] || headerLabel;
              row[columnName] = values[j].trim().replace("\r", "");
            }
          });
          return row.email ? row : null;
        })
        .filter((row): row is Record<string, string> => row !== null);

      this.mergeCsvData(data);
    };
    reader.readAsText(file);
  }

  private parseExcelFile(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        const result = e.target?.result;
        if (!result) return;

        const arrayBuffer = new Uint8Array(result as ArrayBuffer);
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          this.toastr.error(
            this.translate.instant("employeeManager.emptyFile") as string,
          );
          return;
        }

        const headerLabels = (jsonData[0] as string[]).filter((h) => h);
        const headerMap = this.mapHeadersToColumns(headerLabels);
        const excelData = (jsonData.slice(1) as unknown[][])
          .map((row) => {
            const rowData: Record<string, string> = {};
            headerLabels.forEach((headerLabel, index) => {
              if (headerLabel && row[index] !== undefined) {
                const columnName = headerMap[headerLabel] || headerLabel;
                rowData[columnName] =
                  (row[index] as string | number)?.toString() || "";
              }
            });
            return rowData;
          })
          .filter((row) => row.email);

        this.mergeCsvData(excelData);
      } catch (_error) {
        this.toastr.error(
          this.translate.instant("employeeManager.errorParsingFile") as string,
        );
      }
    };
    reader.readAsArrayBuffer(file);
  }

  private mapHeadersToColumns(headerLabels: string[]): Record<string, string> {
    const headerMap: Record<string, string> = {};
    headerLabels.forEach((headerLabel) => {
      const trimmedLabel = headerLabel.trim();
      if (this.availableColumns.includes(trimmedLabel)) {
        headerMap[trimmedLabel] = trimmedLabel;
      } else {
        headerMap[trimmedLabel] = trimmedLabel;
      }
    });
    return headerMap;
  }

  private mergeCsvData(csvData: Record<string, unknown>[]): void {
    let newCount = 0;
    let updatedCount = 0;
    const invalidCount = 0;

    csvData.forEach((csvRow) => {
      const existingIndex = this.employees.findIndex(
        (emp) => emp.email === csvRow.email,
      );

      if (existingIndex > -1) {
        const existingEmployee = this.employees[existingIndex];
        if (this.hasEmployeeChanges(existingEmployee, csvRow)) {
          this.updateEmployeeFromCsv(existingEmployee, csvRow);
          existingEmployee.validationErrors =
            this.validateEmployee(existingEmployee);
          existingEmployee.isEditing = true;
          updatedCount++;
        }
      } else {
        const newEmployee = this.createEmployeeFromCsvRow(csvRow);
        newEmployee.validationErrors = this.validateEmployee(newEmployee);
        newEmployee.isEditing = true;
        this.employees.unshift(newEmployee);
        newCount++;
      }
    });

    this.setupColumns();
    this.updateFilteredEmployees();
    this.showImportResults(newCount, updatedCount, invalidCount);
  }

  private hasEmployeeChanges(
    existingEmployee: EditableEmployee,
    csvRow: Record<string, unknown>,
  ): boolean {
    return Object.keys(csvRow).some((key) => {
      if (key === "email") {
        return false;
      }

      const csvValue = this.normalizeValue(csvRow[key]);
      let existingValue = "";

      if (this.isBasicColumn(key)) {
        existingValue = this.normalizeValue(
          existingEmployee[key as keyof EditableEmployee],
        );
      } else if (this.isDynamicColumn(key)) {
        existingValue = this.normalizeValue(
          existingEmployee.dynamicVariables?.[key],
        );
      } else if (this.isBranchVariableColumn(key)) {
        existingValue = this.normalizeValue(
          existingEmployee.branchVariables?.[key],
        );
      }

      return csvValue !== existingValue;
    });
  }

  private normalizeValue(value: unknown): string {
    if (value === null || value === undefined) {
      return "";
    }
    if (typeof value === "string") {
      return value.trim();
    }
    if (typeof value === "number" || typeof value === "boolean") {
      return String(value).trim();
    }
    return "";
  }

  private updateEmployeeFromCsv(
    existingEmployee: EditableEmployee,
    csvRow: Record<string, unknown>,
  ): void {
    Object.keys(csvRow).forEach((key) => {
      if (key === "email") {
        return;
      }

      const csvValue = csvRow[key];
      const stringValue = this.normalizeValue(csvValue);

      if (this.isBasicColumn(key)) {
        (existingEmployee as unknown as Record<string, string>)[key] =
          stringValue;
      } else if (this.isDynamicColumn(key)) {
        existingEmployee.dynamicVariables =
          existingEmployee.dynamicVariables || {};
        if (stringValue) {
          existingEmployee.dynamicVariables[key] = stringValue;
        }
      } else if (this.isBranchVariableColumn(key)) {
        existingEmployee.branchVariables =
          existingEmployee.branchVariables || {};
        if (stringValue) {
          existingEmployee.branchVariables[key] = stringValue;
        }
      }
    });

    if ("givenName" in csvRow || "familyName" in csvRow) {
      const givenName = String(
        (csvRow["givenName"] as string | number | boolean) ||
          existingEmployee.givenName ||
          "",
      );
      const familyName = String(
        (csvRow["familyName"] as string | number | boolean) ||
          existingEmployee.familyName ||
          "",
      );
      existingEmployee.fullName = `${givenName} ${familyName}`.trim();
    }
  }

  private createEmployeeFromCsvRow(
    csvRow: Record<string, unknown>,
  ): EditableEmployee {
    const givenName = String(
      (csvRow["givenName"] as string | number | boolean) || "",
    );
    const familyName = String(
      (csvRow["familyName"] as string | number | boolean) || "",
    );
    const fullName =
      `${givenName} ${familyName}`.trim() ||
      String((csvRow["email"] as string | number | boolean) || "New Employee");

    const basicFields: Record<string, string> = {};
    const dynamicVariables: Record<string, unknown> = {};
    const branchVariables: Record<string, unknown> = {};

    Object.keys(csvRow).forEach((key) => {
      if (key === "email") {
        return;
      }

      const csvValue = csvRow[key];
      const stringValue = this.normalizeValue(csvValue);

      if (this.isBasicColumn(key)) {
        basicFields[key] = stringValue;
      } else if (this.isDynamicColumn(key)) {
        if (stringValue) {
          dynamicVariables[key] = stringValue;
        }
      } else if (this.isBranchVariableColumn(key)) {
        if (stringValue) {
          branchVariables[key] = stringValue;
        }
      }
    });

    const tempId = `temp-${Date.now()}-${Math.random()}`;
    return {
      employeeId: tempId,
      email: String((csvRow["email"] as string | number | boolean) || ""),
      givenName,
      familyName,
      fullName,
      phoneNumber: basicFields["phoneNumber"] || "",
      jobTitle: basicFields["jobTitle"] || "",
      mobileNumber:
        basicFields["mobileNumber"] || basicFields["phoneNumber"] || "",
      createdAt: new Date(),
      templateId: null,
      dynamicVariables:
        Object.keys(dynamicVariables).length > 0 ? dynamicVariables : {},
      branchVariables:
        Object.keys(branchVariables).length > 0 ? branchVariables : undefined,
      customLinks: [],
      vcard: [],
      badges: [],
      canBeExcludedFromSignatures: true,
      dataSource: "LOCAL",
      isNew: true,
      isEditing: false,
    };
  }

  private showImportResults(
    newCount: number,
    updatedCount: number,
    invalidCount: number,
  ): void {
    if (invalidCount > 0) {
      this.toastr.error(
        this.translate.instant("employeeManager.csvImportedWithErrors", {
          invalidCount,
        }) as string,
      );
    } else if (newCount > 0 || updatedCount > 0) {
      this.toastr.success(
        this.translate.instant("employeeManager.csvImportedWithStats", {
          newCount,
          updatedCount,
          invalidCount,
        }) as string,
      );
    } else {
      this.toastr.info(
        this.translate.instant("employeeManager.csvNoChanges") as string,
      );
    }
  }

  public exportToCsv(): void {
    const csvContent = this.generateCsvContent(this.filteredEmployees);
    this.downloadCsv(csvContent, "employees_export.csv");
  }

  public downloadTemplate(): void {
    const csvContent = this.generateCsvContent([]);
    this.downloadCsv(csvContent, "employee_template.csv");
  }

  private generateCsvContent(employees: EditableEmployee[]): string {
    const headers = this.availableColumns;
    const escapeCsvValue = (value: string): string => {
      if (value.includes(",") || value.includes('"') || value.includes("\n")) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };
    return (
      headers.join(",") +
      "\n" +
      employees
        .map((emp) =>
          headers
            .map((header) =>
              escapeCsvValue(this.getCellValue(emp, header) || ""),
            )
            .join(","),
        )
        .join("\n")
    );
  }

  private downloadCsv(content: string, filename: string): void {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  public getCellValue(employee: EditableEmployee, column: string): string {
    if (this.isBasicColumn(column)) {
      const value = (employee as unknown as Record<string, string>)[column];
      return value || "";
    }

    if (employee.dynamicVariables && column in employee.dynamicVariables) {
      const val = employee.dynamicVariables[column];
      if (val === null || val === undefined) return "";
      if (
        typeof val === "string" ||
        typeof val === "number" ||
        typeof val === "boolean"
      ) {
        return String(val);
      }
      return "";
    }

    if (employee.branchVariables && column in employee.branchVariables) {
      const val = employee.branchVariables[column];
      if (val === null || val === undefined) return "";
      if (
        typeof val === "string" ||
        typeof val === "number" ||
        typeof val === "boolean"
      ) {
        return String(val);
      }
      return "";
    }

    return "";
  }

  public getColumnLabel(column: string): string {
    if (this.isBranchVariableColumn(column)) {
      return this.getBranchVariableLabel(column);
    }
    return (
      this.employeeSettings.find((s) => s.name === column)?.label ||
      (this.translate.instant(
        `employee.signatureVariables.${column}`,
      ) as string)
    );
  }

  private getBranchVariableLabel(varName: string): string {
    for (const branch of this.branches) {
      if (branch.variables && branch.variables[varName]) {
        const branchVar = branch.variables[varName];
        if (branchVar?.label) {
          return this.translate.instant(branchVar.label) as string;
        }
      }
    }
    const defaultTranslationKey = `branchSettings.variableLabels.${varName}`;
    const translated = this.translate.instant(defaultTranslationKey) as string;
    return translated !== defaultTranslationKey ? translated : varName;
  }

  public hasUnsavedChanges(): boolean {
    return this.employees.some((emp) => emp.isEditing || emp.isNew);
  }

  private isDynamicColumn(column: string): boolean {
    return this.employeeSettings.some((setting) => setting.name === column);
  }

  private isBranchVariableColumn(column: string): boolean {
    return this.branchVariableNames.includes(column);
  }

  private isBasicColumn(column: string): boolean {
    return ["email", "givenName", "familyName"].includes(column);
  }

  private hasValueChanged(
    employee: EditableEmployee,
    column: string,
    newValue: unknown,
  ): boolean {
    if (!employee.originalData) {
      return true;
    }

    const normalizedNewValue = this.normalizeValue(newValue);

    if (this.isBranchVariableColumn(column)) {
      const originalValue = this.normalizeValue(
        employee.originalData.branchVariables?.[column],
      );
      return normalizedNewValue !== originalValue;
    } else if (this.isDynamicColumn(column)) {
      const originalValue = this.normalizeValue(
        employee.originalData.dynamicVariables?.[column],
      );
      return normalizedNewValue !== originalValue;
    } else if (this.isBasicColumn(column)) {
      const originalValue = this.normalizeValue(
        (employee.originalData as unknown as Record<string, unknown>)[column],
      );
      return normalizedNewValue !== originalValue;
    }

    return false;
  }

  private isValidEmailDomain(email: string): boolean {
    if (!email || !this.authService.currentDomain) {
      return true;
    }
    return email.split("@")[1] === this.authService.currentDomain;
  }

  private validateEmployee(employee: EditableEmployee): {
    [key: string]: string;
  } {
    const errors: { [key: string]: string } = {};

    if (!employee.email) {
      errors.email = this.translate.instant(
        "employeeManager.emailRequired",
      ) as string;
    } else if (!this.isValidEmailDomain(employee.email)) {
      errors.email = this.translate.instant(
        "employeeManager.invalidEmailDomain",
        { domain: this.authService.currentDomain },
      ) as string;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(employee.email)) {
      errors.email = this.translate.instant(
        "employeeManager.emailInvalid",
      ) as string;
    }

    if (!employee.givenName?.trim()) {
      errors.givenName = this.translate.instant(
        "employeeManager.givenNameRequired",
      ) as string;
    }

    if (!employee.familyName?.trim()) {
      errors.familyName = this.translate.instant(
        "employeeManager.familyNameRequired",
      ) as string;
    }

    return errors;
  }

  public hasValidationError(
    employee: EditableEmployee,
    field: string,
  ): boolean {
    return !!(employee.validationErrors && employee.validationErrors[field]);
  }

  public getValidationError(employee: EditableEmployee, field: string): string {
    return employee.validationErrors?.[field] || "";
  }

  public hasAnyValidationErrors(employee: EditableEmployee): boolean {
    return !!(
      employee.validationErrors && Object.keys(employee.validationErrors).length
    );
  }

  public hasValidationErrorsInUnsaved(): boolean {
    return this.employees.some(
      (emp) => (emp.isEditing || emp.isNew) && this.hasAnyValidationErrors(emp),
    );
  }

  public canDeleteEmployee(employee: EditableEmployee): boolean {
    if (employee.isNew) {
      return true;
    }

    if (employee.signatureInstallationAt) {
      return false;
    }

    const isLocalEmployee = employee.dataSource === "LOCAL";
    return this.isSuperAdmin || (this.hasRole && isLocalEmployee);
  }

  public canEditEmail(employee: EditableEmployee): boolean {
    return (
      employee.isNew ||
      employee.dataSource === "LOCAL" ||
      employee.dataSource === "SAMPLE"
    );
  }

  public getCellTooltip(employee: EditableEmployee, column: string): string {
    if (this.hasValidationError(employee, column)) {
      return this.getValidationError(employee, column);
    }
    return "";
  }

  private cleanEmployeeForApi(employee: EditableEmployee): Employee {
    const {
      isEditing: _isEditing,
      isNew: _isNew,
      originalData: _originalData,
      validationErrors: _validationErrors,
      isSaving: _isSaving,
      isDeleting: _isDeleting,
      ...cleanEmployee
    } = employee;
    if (
      employee.isNew ||
      !["GOOGLE", "MICROSOFT", "LOCAL", "SAMPLE"].includes(
        cleanEmployee.dataSource,
      )
    ) {
      cleanEmployee.dataSource = "LOCAL";
    }
    if (!cleanEmployee.dynamicVariables) {
      cleanEmployee.dynamicVariables = {};
    }
    if (
      employee.dataSource === "GOOGLE" &&
      JSON.stringify(cleanEmployee.dynamicVariables) === "{}"
    ) {
      delete (cleanEmployee as Partial<Employee>).dynamicVariables;
    }
    if (
      !cleanEmployee.branchVariables ||
      Object.keys(cleanEmployee.branchVariables).length === 0
    ) {
      cleanEmployee.branchVariables = undefined;
    }
    cleanEmployee.givenName = cleanEmployee.givenName || null;
    cleanEmployee.familyName = cleanEmployee.familyName || null;
    cleanEmployee.fullName = cleanEmployee.fullName || null;
    cleanEmployee.userAbout = cleanEmployee.userAbout || null;
    cleanEmployee.templateId = cleanEmployee.templateId || null;
    cleanEmployee.internalTemplateId = cleanEmployee.internalTemplateId || null;
    cleanEmployee.branchId = cleanEmployee.branchId || null;
    cleanEmployee.thumbnailPhotoUrl = cleanEmployee.thumbnailPhotoUrl || null;
    if (cleanEmployee.dynamicVariables) {
      cleanEmployee.dynamicVariables = Object.fromEntries(
        Object.entries(cleanEmployee.dynamicVariables).map(([k, v]) => [
          k,
          v || null,
        ]),
      );
    }
    if (cleanEmployee.branchVariables) {
      cleanEmployee.branchVariables = Object.fromEntries(
        Object.entries(cleanEmployee.branchVariables).map(([k, v]) => [
          k,
          v || null,
        ]),
      );
    }
    return cleanEmployee;
  }

  public close(): void {
    this.dialogRef.close();
  }
}
